export interface CityLocation {
  slug: string;
  name: string;
  region: string;
  tagline: string;
  phone: string;
  phoneFormatted: string;
  /** Human-readable full address for display (footer, map tooltip, llms.txt). */
  address: string;
  /**
   * Structured address fields for Schema.org PostalAddress JSON-LD.
   * `addressLocality` is the *real* city of the verified office, which may
   * differ from the page brand (e.g. Dallas page → Farmers Branch office).
   * Google cross-references this against the Google Business Profile.
   */
  postalAddress: {
    streetAddress: string;
    addressLocality: string;
    addressRegion: string;
    postalCode: string;
    addressCountry: string;
  };
  mapLink: string;
  heroHeading: string;
  heroSub: string;
  lat: number;
  lng: number;
}

type GeoPoint = readonly [lng: number, lat: number];

export const locations: Record<string, CityLocation> = {
  houston: {
    slug: "houston",
    name: "Houston",
    region: "Greater Houston Area",
    tagline: "Houston Roofing Contractor",
    phone: "+13463393405",
    phoneFormatted: "(346) 339-3405",
    address: "11767 Katy Fwy, Ste 600, Houston, TX 77079",
    postalAddress: {
      streetAddress: "11767 Katy Fwy, Ste 600",
      addressLocality: "Houston",
      addressRegion: "TX",
      postalCode: "77079",
      addressCountry: "US",
    },
    mapLink: "https://maps.app.goo.gl/M2yao4sV4cAsfAL2A",
    heroHeading: "Houston\u2019s Trusted Roof Repair & Replacement Experts",
    heroSub: "Serving Katy, Sugar Land, The Woodlands, Victoria & Greater Houston.",
    lat: 29.7604,
    lng: -95.3698,
  },
  dallas: {
    slug: "dallas",
    name: "Dallas",
    region: "Dallas-Fort Worth Metroplex",
    tagline: "Dallas-Fort Worth's Trusted Roofing Experts",
    // Dallas-Fort Worth office line, per the client's Dallas page doc + GBP.
    phone: "+18443417663",
    phoneFormatted: "(844) 341-7663",
    address: "13500 Midway Rd, Suite 410, Farmers Branch, TX 75244",
    postalAddress: {
      streetAddress: "13500 Midway Rd, Suite 410",
      addressLocality: "Farmers Branch",
      addressRegion: "TX",
      postalCode: "75244",
      addressCountry: "US",
    },
    mapLink: "https://maps.app.goo.gl/hg2yuHJwQLsKxKLE6",
    heroHeading: "Dallas\u2013Fort Worth\u2019s Trusted Roof Repair & Replacement Experts",
    heroSub: "Serving Fort Worth, Arlington, Plano, Tyler, Sherman & the DFW Metroplex.",
    lat: 32.7767,
    lng: -96.7970,
  },
  austin: {
    slug: "austin",
    name: "Austin / San Antonio",
    region: "Central Texas",
    tagline: "Central Texas' Trusted Roofing Professionals",
    // Austin-San Antonio office line (per the homepage NAP + Austin/San Antonio docs).
    phone: "+15122211858",
    phoneFormatted: "(512) 221-1858",
    address: "1067 FM306, Suite 104, New Braunfels, TX 78130",
    postalAddress: {
      streetAddress: "1067 FM306, Suite 104",
      addressLocality: "New Braunfels",
      addressRegion: "TX",
      postalCode: "78130",
      addressCountry: "US",
    },
    mapLink: "https://maps.app.goo.gl/7TBCpKq2LTHGcbNz8",
    heroHeading: "Austin & San Antonio\u2019s Trusted Roof Repair & Replacement Experts",
    heroSub: "Serving Austin, San Antonio, Killeen, Temple & Central Texas.",
    lat: 30.2672,
    lng: -97.7431,
  },
};

export const defaultCity = "houston";

const TEXAS_BOUNDARY: readonly GeoPoint[] = [
  [-94.04, 33.57],
  [-94.48, 33.02],
  [-94.68, 31.0],
  [-93.82, 29.76],
  [-94.9, 29.1],
  [-96.0, 28.6],
  [-97.18, 27.5],
  [-97.4, 25.84],
  [-99.15, 26.95],
  [-100.25, 29.1],
  [-101.4, 29.78],
  [-103.07, 29.78],
  [-104.92, 30.17],
  [-106.63, 31.79],
  [-103.04, 31.79],
  [-103.04, 36.5],
  [-100.0, 36.5],
  [-100.0, 34.56],
  [-99.0, 34.56],
  [-98.0, 34.45],
  [-97.0, 33.9],
  [-96.2, 33.84],
  [-95.2, 33.84],
];

function isPointInPolygon(lat: number, lng: number, polygon: readonly GeoPoint[]) {
  let inside = false;

  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [currentLng, currentLat] = polygon[i];
    const [previousLng, previousLat] = polygon[j];
    const crossesLatitude = currentLat > lat !== previousLat > lat;

    if (!crossesLatitude) continue;

    const edgeLng =
      ((previousLng - currentLng) * (lat - currentLat)) /
        (previousLat - currentLat) +
      currentLng;

    if (lng < edgeLng) {
      inside = !inside;
    }
  }

  return inside;
}

export function isInTexas(lat: number, lng: number): boolean {
  if (lat < 25.8 || lat > 36.5 || lng < -106.7 || lng > -93.5) {
    return false;
  }

  return isPointInPolygon(lat, lng, TEXAS_BOUNDARY);
}

export function findNearestCity(lat: number, lng: number): string {
  let nearest = defaultCity;
  let minDist = Infinity;

  for (const [slug, loc] of Object.entries(locations)) {
    const d = Math.hypot(lat - loc.lat, lng - loc.lng);
    if (d < minDist) {
      minDist = d;
      nearest = slug;
    }
  }
  return nearest;
}
